import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import {
  CreditCard, Landmark, Banknote, CheckCircle, Clock, AlertCircle,
  Receipt, Loader2, X, Upload, FileText, ArrowLeft, Wallet
} from 'lucide-react';

// ---------- Types ----------
interface Charge {
  id: string;
  description: string;
  amount: number;
  currency: string;
  due_date: string;
  status: 'pending' | 'paid' | 'overdue' | 'partial';
  charge_type?: string;
  unit_id?: string;
  balance?: number;
}

interface PaymentRecord {
  id: string;
  receipt_number?: string;
  amount: number;
  currency: string;
  payment_method: string;
  status: string;
  created_at: string;
  reference_number?: string;
}

type PaymentMethod = 'card' | 'transfer' | 'cash';

// ---------- Component ----------
export default function Pagos() {
  const { user } = useAuth();
  const condoId = user?.condoId ?? '';

  const [charges, setCharges] = useState<Charge[]>([]);
  const [payments, setPayments] = useState<PaymentRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [forbidden, setForbidden] = useState(false);

  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedCharge, setSelectedCharge] = useState<Charge | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('card');
  const [proofFile, setProofFile] = useState<File | null>(null);
  const [referenceNumber, setReferenceNumber] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [paymentResult, setPaymentResult] = useState<any>(null);
  const [showSuccess, setShowSuccess] = useState(false);

  // Load pending charges + payment history
  const loadData = useCallback(async () => {
    if (!condoId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    setForbidden(false);
    try {
      const privilegedRoles = ['admin', 'super_admin', 'treasurer'];
      const isPrivileged = privilegedRoles.includes(user?.role ?? '');
      if (isPrivileged) {
        const [chargesRes, paymentsRes] = await Promise.all([
          api.get(`/financial/condo/${condoId}/charges`, { params: { status: 'pending,overdue,partial' } }),
          api.get(`/financial/payments/condo/${condoId}`),
        ]);
        const chargesData = chargesRes.data?.data ?? chargesRes.data ?? [];
        const paymentsData = paymentsRes.data?.data ?? paymentsRes.data ?? [];
        setCharges(Array.isArray(chargesData) ? chargesData : (chargesData?.charges ?? []));
        setPayments(Array.isArray(paymentsData) ? paymentsData : (paymentsData?.payments ?? []));
      } else {
        // Resident account reads are user-scoped; do not call condo-wide financial endpoints.
        const accountRes = await api.get(`/financial/condo/${condoId}/account/${user?.id}`);
        const account = accountRes.data?.data ?? accountRes.data ?? {};
        setCharges(Array.isArray(account.charges) ? account.charges.filter((charge: Charge) =>
          ['pending', 'overdue', 'partial'].includes(charge.status)
        ) : []);
        setPayments(Array.isArray(account.payments) ? account.payments : []);
      }
    } catch (err: any) {
      console.error('Error loading payment data:', err);
      if (err?.response?.status === 403) setForbidden(true);
      else if (err?.response?.status === 404) setError('No encontramos la información de pagos de tu cuenta. Intenta de nuevo más tarde.');
      else setError('No se pudieron cargar los pagos. Intenta de nuevo.');
      setCharges([]);
      setPayments([]);
    } finally {
      setLoading(false);
    }
  }, [condoId, user?.unit]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const openPaymentModal = (charge: Charge) => {
    setSelectedCharge(charge);
    setPaymentMethod('card');
    setProofFile(null);
    setReferenceNumber('');
    setPaymentResult(null);
    setShowPaymentModal(true);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (file.size > 5 * 1024 * 1024) {
        alert('El archivo no puede exceder 5MB');
        return;
      }
      setProofFile(file);
    }
  };

  const handlePayment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedCharge) return;

    setSubmitting(true);
    setError(null);

    try {
      const amount = selectedCharge.balance ?? selectedCharge.amount;

      if (paymentMethod === 'card') {
        // Route to Openpay checkout with charge context
        const params = new URLSearchParams({
          plan: 'charge',
          chargeId: selectedCharge.id,
          amount: String(amount),
          currency: selectedCharge.currency || 'MXN',
          description: selectedCharge.description,
          condoId,
        });
        window.location.href = `/billing/checkout?${params.toString()}`;
        return;
      }

      // Transfer / Cash — upload proof + reference.
      // Backend parses JSON only, so convert the proof File to base64
      // (same pattern as expensesAPI.uploadReceiptBase64).
      let proofImageBase64: string | undefined;
      let proofMimeType: string | undefined;
      if (proofFile) {
        proofImageBase64 = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve((reader.result as string).split(',')[1]);
          reader.onerror = () => reject(new Error('No se pudo leer el archivo'));
          reader.readAsDataURL(proofFile);
        });
        proofMimeType = proofFile.type || 'image/jpeg';
      }

      const res = await api.post('/financial/payments', {
        charge_id: selectedCharge.id,
        unit_id: selectedCharge.unit_id || user?.unit || '',
        amount,
        currency: selectedCharge.currency || 'MXN',
        payment_method: paymentMethod,
        reference_number: referenceNumber,
        proof_image_base64: proofImageBase64,
        mime_type: proofMimeType,
        notes: `Pago de: ${selectedCharge.description}`,
      });

      setPaymentResult(res.data?.data ?? res.data);
      setShowSuccess(true);
      setShowPaymentModal(false);
      await loadData();
    } catch (err: any) {
      console.error('Payment error:', err);
      const msg = err?.response?.data?.error || err?.response?.data?.message || 'Error al procesar el pago. Intenta de nuevo.';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const formatCurrency = (amount: number, currency: string = 'MXN') => {
    try {
      return new Intl.NumberFormat('es-MX', { style: 'currency', currency }).format(amount);
    } catch {
      return `$${amount.toFixed(2)} ${currency}`;
    }
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '—';
    try {
      return new Date(dateStr).toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  const getChargeStatusBadge = (status: string) => {
    switch (status) {
      case 'overdue':
        return { label: 'Vencido', class: 'bg-red-100 text-red-700 border-red-200' };
      case 'pending':
        return { label: 'Pendiente', class: 'bg-amber-100 text-amber-700 border-amber-200' };
      case 'partial':
        return { label: 'Parcial', class: 'bg-blue-100 text-blue-700 border-blue-200' };
      case 'paid':
        return { label: 'Pagado', class: 'bg-emerald-100 text-emerald-700 border-emerald-200' };
      default:
        return { label: status, class: 'bg-gray-100 text-gray-600 border-gray-200' };
    }
  };

  // ---------- Render ----------
  if (loading) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold text-[var(--v-text-primary)]">Pagos</h2>
          <p className="text-[var(--v-text-secondary)] mt-1">Tus cargos y historial de pagos</p>
        </div>
        <div className="bg-[var(--v-surface-1)] rounded-xl border border-[var(--v-border-light)] p-12 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-[var(--v-text-secondary)] mt-4">Cargando cargos...</p>
        </div>
      </div>
    );
  }

  if (!condoId) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold text-[var(--v-text-primary)]">Pagos</h2>
          <p className="text-[var(--v-text-secondary)] mt-1">Tus cargos y historial de pagos</p>
        </div>
        <div className="bg-[var(--v-surface-1)] rounded-xl border border-[var(--v-border-light)] p-12 text-center">
          <Wallet className="w-16 h-16 text-[var(--v-text-tertiary)] mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-white mb-2">Sin condominio asignado</h3>
          <p className="text-[var(--v-text-secondary)]">Aún no se te ha asignado un condominio. Contacta al administrador.</p>
        </div>
      </div>
    );
  }

  if (forbidden || error) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold text-[var(--v-text-primary)]">Pagos</h2>
          <p className="text-[var(--v-text-secondary)] mt-1">Tus cargos e historial de pagos</p>
        </div>
        <div className={`bg-[var(--v-surface-1)] rounded-xl border p-12 text-center ${forbidden ? 'border-amber-200' : 'border-red-200'}`}>
          <AlertCircle className={`w-16 h-16 mx-auto mb-4 ${forbidden ? 'text-amber-400' : 'text-red-400'}`} />
          <h3 className="text-lg font-semibold text-white mb-2">{forbidden ? 'Sin acceso a esta información' : 'No pudimos cargar tus pagos'}</h3>
          <p className="text-[var(--v-text-secondary)] mb-5">{forbidden ? 'Los pagos del condominio solo están disponibles para la administración.' : error}</p>
          {!forbidden && <button onClick={loadData} className="px-4 py-2.5 bg-[var(--v-info)] hover:bg-blue-700 text-white rounded-lg font-medium">Reintentar</button>}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 min-w-0">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-[var(--v-text-primary)]">Pagos</h2>
        <p className="text-[var(--v-text-secondary)] mt-1">Tus cargos pendientes e historial de pagos</p>
      </div>

    {/* Pending Charges */}
    <div className="bg-[var(--v-surface-1)] rounded-xl border border-[var(--v-border-light)] shadow-sm overflow-hidden">
      <div className="p-5 border-b border-[var(--v-border-light)]">
        <h3 className="font-bold text-white flex items-center gap-2">
          <Receipt className="w-5 h-5 text-[var(--v-info)]" />
          Cargos Pendientes
        </h3>
      </div>

      {charges.length === 0 ? (
        <div className="p-12 text-center">
          <CheckCircle className="w-16 h-16 text-emerald-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-white mb-2">No tienes cargos pendientes</h3>
          <p className="text-[var(--v-text-secondary)]">Estás al corriente con tus pagos 🎉</p>
        </div>
      ) : (
        <div className="divide-y divide-[var(--v-border-light)]">
          {charges.map((charge) => {
            const status = getChargeStatusBadge(charge.status);
            const displayAmount = charge.balance ?? charge.amount;
            return (
              <div key={charge.id} className="p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-start gap-4">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                    charge.status === 'overdue' ? 'bg-red-100' : 'bg-amber-100'
                  }`}>
                    <Receipt className={`w-6 h-6 ${charge.status === 'overdue' ? 'text-red-600' : 'text-amber-600'}`} />
                  </div>
                  <div>
                    <h4 className="font-semibold text-white">{charge.description}</h4>
                    <div className="flex flex-wrap items-center gap-3 mt-1 text-sm text-[var(--v-text-secondary)]">
                      <span>{formatCurrency(displayAmount, charge.currency)}</span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        Vence: {formatDate(charge.due_date)}
                      </span>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${status.class}`}>
                        {status.label}
                      </span>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => openPaymentModal(charge)}
                  className="px-6 py-2.5 bg-[var(--v-info)] hover:bg-blue-700 text-white rounded-lg font-medium transition-all flex items-center justify-center gap-2 flex-shrink-0"
                >
                  Pagar
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>

    {/* Payment History */}
    {payments.length > 0 && (
      <div className="bg-[var(--v-surface-1)] rounded-xl border border-[var(--v-border-light)] shadow-sm overflow-hidden">
        <div className="p-5 border-b border-[var(--v-border-light)]">
          <h3 className="font-bold text-white flex items-center gap-2">
            <FileText className="w-5 h-5 text-[var(--v-info)]" />
            Historial de Pagos
          </h3>
        </div>
        <div className="divide-y divide-[var(--v-border-light)]">
          {payments.slice(0, 10).map((payment) => (
            <div key={payment.id} className="p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <div>
                <div className="flex items-center gap-3">
                  <span className="font-medium text-[var(--v-text-primary)]">
                    {formatCurrency(payment.amount, payment.currency)}
                  </span>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                    payment.status === 'paid' || payment.status === 'verified'
                      ? 'bg-emerald-100 text-emerald-700'
                      : payment.status === 'pending_verification'
                      ? 'bg-amber-100 text-amber-700'
                      : 'bg-gray-100 text-gray-600'
                  }`}>
                    {payment.status === 'paid' || payment.status === 'verified' ? 'Verificado' :
                     payment.status === 'pending_verification' ? 'En revisión' : payment.status}
                  </span>
                </div>
                <div className="text-sm text-[var(--v-text-secondary)] mt-1">
                  {payment.payment_method === 'card' ? 'Tarjeta' : payment.payment_method === 'transfer' ? 'Transferencia' : 'Efectivo'}
                  {payment.receipt_number && ` · Recibo #${payment.receipt_number}`}
                  {payment.reference_number && ` · Ref: ${payment.reference_number}`}
                </div>
              </div>
              <div className="text-sm text-[var(--v-text-tertiary)]">
                {formatDate(payment.created_at)}
              </div>
            </div>
          ))}
        </div>
      </div>
    )}

    {/* ---------- Payment Modal ---------- */}
    {showPaymentModal && selectedCharge && (
      <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50 p-4">
        <div className="bg-[var(--v-surface-1)] rounded-2xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6 border-b border-[var(--v-border-light)] flex items-center justify-between">
            <div>
              <h3 className="text-xl font-semibold text-white">Pagar cargo</h3>
              <p className="text-[var(--v-text-secondary)] text-sm mt-1">{selectedCharge.description}</p>
            </div>
            <button onClick={() => setShowPaymentModal(false)} className="text-[var(--v-text-secondary)] hover:text-[var(--v-text-primary)]">
              <X className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={handlePayment} className="p-6 space-y-5">
            {/* Amount */}
            <div className="bg-[var(--v-base-bg)] p-4 rounded-xl">
              <div className="flex items-center justify-between">
                <span className="text-sm text-[var(--v-text-secondary)]">Monto a pagar</span>
                <span className="text-2xl font-bold text-[var(--v-text-primary)]">
                  {formatCurrency(selectedCharge.balance ?? selectedCharge.amount, selectedCharge.currency)}
                </span>
              </div>
            </div>

            {/* Payment Method Selector */}
            <div>
              <label className="block text-sm font-medium text-[var(--v-text-primary)] mb-3">Método de pago</label>
              <div className="space-y-2">
                <MethodOption
                  icon={<CreditCard className="w-5 h-5" />}
                  label="Tarjeta"
                  description="Visa, Mastercard, American Express (Openpay)"
                  selected={paymentMethod === 'card'}
                  onClick={() => setPaymentMethod('card')}
                />
                <MethodOption
                  icon={<Landmark className="w-5 h-5" />}
                  label="Transferencia"
                  description="Transferencia bancaria (SPEI)"
                  selected={paymentMethod === 'transfer'}
                  onClick={() => setPaymentMethod('transfer')}
                />
                <MethodOption
                  icon={<Banknote className="w-5 h-5" />}
                  label="Efectivo"
                  description="Pago en efectivo (OXXO o administración)"
                  selected={paymentMethod === 'cash'}
                  onClick={() => setPaymentMethod('cash')}
                />
              </div>
            </div>

            {/* Transfer / Cash: proof + reference */}
            {paymentMethod !== 'card' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-[var(--v-text-primary)] mb-2">
                    Número de referencia {paymentMethod === 'cash' && '(opcional)'}
                  </label>
                  <input
                    type="text"
                    value={referenceNumber}
                    onChange={(e) => setReferenceNumber(e.target.value)}
                    className="w-full px-4 py-2.5 border border-[var(--v-border-light)] rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-[var(--v-surface-1)] text-[var(--v-text-primary)]"
                    placeholder={paymentMethod === 'transfer' ? 'Folio de transferencia' : 'Folio de pago (opcional)'}
                    required={paymentMethod === 'transfer'}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[var(--v-text-primary)] mb-2">
                    Comprobante de pago {paymentMethod === 'transfer' && '(obligatorio)'}
                  </label>
                  <div className="border-2 border-dashed border-[var(--v-border-strong)] rounded-lg p-6 text-center">
                    {proofFile ? (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-[var(--v-text-primary)] truncate">{proofFile.name}</span>
                        <button type="button" onClick={() => setProofFile(null)} className="text-red-500 hover:text-red-700">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <>
                        <Upload className="w-8 h-8 text-[var(--v-text-tertiary)] mx-auto mb-2" />
                        <input
                          type="file"
                          accept="image/*,.pdf"
                          onChange={handleFileChange}
                          className="hidden"
                          id="proof-upload"
                          required={paymentMethod === 'transfer'}
                        />
                        <label htmlFor="proof-upload" className="cursor-pointer text-sm text-[var(--v-info)] hover:underline">
                          Subir comprobante (imagen o PDF, máx 5MB)
                        </label>
                      </>
                    )}
                  </div>
                  {paymentMethod !== 'transfer' && (
                    <p className="text-xs text-[var(--v-text-tertiary)] mt-1">
                      Tu pago quedará como "pendiente de verificación" hasta que el administrador lo confirme.
                    </p>
                  )}
                </div>
              </>
            )}

            {error && (
              <div className="p-3 rounded-lg bg-red-50 border border-red-200 flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                <span className="text-sm text-red-700">{error}</span>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={() => setShowPaymentModal(false)}
                disabled={submitting}
                className="flex-1 px-4 py-2.5 border border-[var(--v-border-light)] text-[var(--v-text-primary)] rounded-lg font-medium hover:bg-[var(--v-base-bg)] transition-colors"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="flex-1 px-4 py-2.5 bg-[var(--v-info)] hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Procesando...
                  </>
                ) : paymentMethod === 'card' ? (
                  <>
                    <CreditCard className="w-4 h-4" />
                    Pagar con tarjeta
                  </>
                ) : (
                  'Enviar comprobante'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    )}

    {/* ---------- Success Screen ---------- */}
    {showSuccess && paymentResult && (
      <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50 p-4">
        <div className="bg-[var(--v-surface-1)] rounded-2xl shadow-xl max-w-md w-full p-8 text-center">
          <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-emerald-600" />
          </div>
          <h2 className="text-2xl font-bold text-[var(--v-text-primary)] mb-2">¡Pago enviado!</h2>
          <p className="text-[var(--v-text-secondary)] mb-6">
            {paymentResult.status === 'paid'
              ? 'Tu pago ha sido confirmado.'
              : 'Tu pago está pendiente de verificación por el administrador.'}
          </p>
          {paymentResult.receipt_number && (
            <div className="bg-[var(--v-base-bg)] p-4 rounded-xl mb-6">
              <p className="text-sm text-[var(--v-text-secondary)]">Número de recibo</p>
              <p className="text-xl font-bold font-mono text-[var(--v-text-primary)]">#{paymentResult.receipt_number}</p>
            </div>
          )}
          <button
            onClick={() => { setShowSuccess(false); setPaymentResult(null); }}
            className="w-full px-4 py-2.5 bg-[var(--v-info)] hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
          >
            Aceptar
          </button>
        </div>
      </div>
    )}
    </div>
  );
}

// ---------- Method Option Component ----------
function MethodOption({ icon, label, description, selected, onClick }: {
  icon: React.ReactNode;
  label: string;
  description: string;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full flex items-center gap-4 p-4 rounded-lg border-2 transition-all text-left ${
        selected
          ? 'border-[var(--v-info)] bg-blue-50'
          : 'border-[var(--v-border-light)] hover:border-[var(--v-border-strong)]'
      }`}
    >
      <div className={`flex-shrink-0 ${selected ? 'text-[var(--v-info)]' : 'text-[var(--v-text-secondary)]'}`}>
        {icon}
      </div>
      <div className="flex-1">
        <div className="font-medium text-[var(--v-text-primary)]">{label}</div>
        <div className="text-sm text-[var(--v-text-secondary)]">{description}</div>
      </div>
      <div className={`w-5 h-5 rounded-full border-2 flex-shrink-0 ${
        selected ? 'border-[var(--v-info)] bg-[var(--v-info)]' : 'border-[var(--v-border-strong)]'
      }`}>
        {selected && <CheckCircle className="w-full h-full text-white p-0.5" />}
      </div>
    </button>
  );
}
